import { AppNS, Dcra } from 'app';
import { request } from 'app/api_v2/config';
import { API_URL } from 'app/core/config';
import axios, { Canceler } from 'axios';

import { getErrorMessage, getErrorMessagesAsArray } from '../helpers/errorHandling';
import { prepareJobData } from './inspections-parsers';

const CancelToken = axios.CancelToken;
let cancel: Canceler;

export function apiSendNewJob(reqData: Dcra.Inspection): Promise<string> {
  return new Promise((resolve, reject) => {
    axios
      .post(`${API_URL}/inspections.json`, prepareJobData(reqData))
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model.id);
        } else {
          reject(getErrorMessage(data));
        }
      })
      .catch((error) => {
        reject(getErrorMessage(error));
      });
  });
}

export function getInspection(id: string): Promise<Dcra.Inspection> {
  return new Promise((resolve, reject) => {
    axios
      .get(`${API_URL}/inspections/${id}.json`)
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model);
        } else {
          reject(getErrorMessage(data));
        }
      })
      .catch((error) => {
        return reject(error.response);
      });
  });
}

export function getInspectionsForListing(params: AppNS.Params, signal?: AbortSignal): Promise<Dcra.InspectionResult> {
  return request(
    {
      url: `inspections.json`,
      method: 'GET',
      baseURL: API_URL,
      params: {
        ...params,
        per_page: params.perPage || 20,
        with_permits: true,
      },
      signal,
    },
    (data) => data,
    { errorType: 'legacy' }
  );
}

export function getInspections(params: AppNS.Params): Promise<Dcra.InspectionResult> {
  if (cancel !== undefined) {
    cancel();
  }
  return new Promise((resolve, reject) => {
    axios
      .get(`${API_URL}/inspections.json`, {
        cancelToken: new CancelToken(function executor(c) {
          // An executor function receives a cancel function as a parameter
          cancel = c;
        }),
        params: { ...params, with_permits: true },
      })
      .then(({ data }) => {
        if (data) {
          resolve(data);
        } else {
          reject(getErrorMessage(data));
        }
      })
      .catch((error) => {
        return reject(error.response);
      });
  });
}

export function updateResults(params: AppNS.Params): Promise<Dcra.Inspection> {
  return new Promise((resolve, reject) => {
    axios
      .patch(`${API_URL}/inspections/update-results.json`, {
        ...params,
      })
      .then(({ data }) => {
        if (data) {
          resolve(data.model);
        } else {
          reject(data.errors);
        }
      })
      .catch((error) => {
        return reject(error.response);
      });
  });
}

export type InspectionUpdateAction =
  | 'update-permits'
  | 'update-inspector'
  | 'update-date'
  | 'update-contact'
  | 'cancel-by-admin'
  | 'cancel-by-client'
  | 'cancel-by-inspector'
  | 'update-client';

export function apiUpdateInspection(
  id: string,
  action: InspectionUpdateAction,
  reqData: AppNS.Params
): Promise<Dcra.Inspection> {
  return new Promise((resolve, reject) => {
    axios
      .patch(`${API_URL}/inspections/${action}.json`, {
        id,
        properties: reqData,
      })
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model);
        } else {
          reject(data.errors);
        }
      })
      .catch((error) => {
        reject(getErrorMessagesAsArray(error));
      });
  });
}

export function apiUpdateInspectionStatus(
  id: string,
  action: 'check_in' | 'check_out',
  params: {
    lat: string;
    lng: string;
    unsafeConditions?: boolean;
    unsafeConditionsComment?: string;
    localizationError?: string;
  }
): Promise<Dcra.Inspection> {
  return new Promise((resolve, reject) => {
    const { lat, lng, unsafeConditions, unsafeConditionsComment, localizationError } = params;
    axios
      .post(`${API_URL}/dcra/check-ins.json`, {
        properties: {
          inspection_id: id,
          type: action,
          lat,
          lng,
          unsafeConditions,
          unsafeConditionsComment,
          localizationError,
        },
      })
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model);
        } else {
          reject(data.errors);
        }
      })
      .catch((error) => {
        return reject(error.response.data.errors);
      });
  });
}

export function apiSaveInspectionQuestionnaire(
  id: string,
  inspectionQuestionnaire: AppNS.Params
): Promise<AppNS.Params> {
  const req = {
    id,
    properties: {
      inspectionQuestionnaire,
    },
  };
  return new Promise((resolve, reject) => {
    axios
      .patch(`${API_URL}/inspections/inspection-questionnaire.json`, req)
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model);
        } else {
          reject(data.errors);
        }
      })
      .catch((error) => {
        return reject(error.response.data.errors);
      });
  });
}

export function apiSaveReportChecklist(id: string, inspectionChecklist: AppNS.Params): AppNS.Params {
  const req = {
    id,
    properties: {
      inspectionChecklist,
    },
  };
  return new Promise((resolve, reject) => {
    axios
      .patch(`${API_URL}/inspections/inspection-checklist.json`, req)
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model);
        } else {
          reject(data.errors);
        }
      })
      .catch((error) => {
        return reject(error.response.data.errors);
      });
  });
}

export function apiSubmitReport(report: Dcra.Inspection): Promise<AppNS.Params> {
  return new Promise((resolve, reject) => {
    axios
      .patch(`${API_URL}/inspections/submit-report.json`, {
        id: report.id,
      })
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model);
        } else {
          reject(data.errors);
        }
      })
      .catch((error) => {
        return reject(error.response.data.errors);
      });
  });
}
